import { apiErrorMessage } from '@agos/api-client'
import type { Category, Product } from '@agos/api-client/model'
import { T, formatCount, tt } from '@agos/shared'
import { BUTTON_SECONDARY, EmptyState, cn, toast } from '@agos/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Edit2, Layers, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ItemEditor } from './ItemEditor'
import { ReorderList } from './ReorderList'
import {
  refreshUpsellReads,
  saveUpsellItemOrder,
  type UpsellGroup,
  type UpsellItem,
} from './upsell-api'
import { checklistGroups, itemsInPromptOrder } from './upsell-rules'

/** Which add-on is open, and under which group - the groups tab opens one under its own group. */
export interface Editing {
  addonProductId: number
  groupId: number | null
}

/**
 * Every add-on in the order a prompt offers it - `FRONTEND.md` §9.711.
 *
 * **The order is saved on every move**, one `PUT /upsell/items/order` with the whole list, and the list holds the
 * moved order only until the server's order has arrived. Moves are refused while one is in flight, because two
 * `PUT`s racing each other would leave whichever landed second, which need not be the last one made.
 */
export function ItemsPanel({
  items,
  groups,
  products,
  categories,
  editing,
  onEdit,
}: {
  items: readonly UpsellItem[]
  groups: readonly UpsellGroup[]
  products: readonly Product[]
  categories: readonly Category[]
  editing: Editing | null
  onEdit: (next: Editing | null) => void
}) {
  const queryClient = useQueryClient()
  const nameOf = useMemo(() => {
    const names = new Map(products.map((product) => [product.id, product.name ?? '']))
    return (id: number) => names.get(id) ?? `#${String(id)}`
  }, [products])

  const ordered = useMemo(() => itemsInPromptOrder(items, nameOf), [items, nameOf])
  const [moved, setMoved] = useState<UpsellItem[] | null>(null)
  const shown = moved ?? ordered

  const order = useMutation({
    mutationFn: (next: UpsellItem[]) => saveUpsellItemOrder(next.map((item) => item.addonProductId)),
    onSuccess: async () => {
      await refreshUpsellReads(queryClient)
      setMoved(null)
    },
    onError: (caught) => {
      setMoved(null)
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Poradie doplnkov sa nepodarilo uložiť.')),
      })
    },
  })

  const reorder = (next: UpsellItem[]) => {
    setMoved(next)
    order.mutate(next)
  }

  // The catalogue minus what is already an add-on, for *Pridať doplnok*.
  const offered = useMemo(() => {
    const already = new Set(items.map((item) => item.addonProductId))
    return checklistGroups(products, categories, '', already)
  }, [items, products, categories])
  const [picked, setPicked] = useState('')

  const groupName = (id: number) => groups.find((group) => group.id === id)?.name ?? `#${String(id)}`
  const open = editing === null ? undefined : items.find((item) => item.addonProductId === editing.addonProductId)

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-2" data-upsell-add>
          <label className="flex-1 min-w-[12rem] space-y-1 text-sm">
            <span className="font-semibold">
              <T>Pridať doplnok z katalógu</T>
            </span>
            <select
              value={picked}
              onChange={(event) => setPicked(event.target.value)}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
              data-upsell-add-select
            >
              <option value="">{tt('Vyberte produkt...')}</option>
              {offered.map((group) => (
                <optgroup key={group.key} label={tt(group.category)}>
                  {group.products.map((product) => (
                    <option key={product.id} value={String(product.id)}>
                      {tt(product.name)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={picked === ''}
            onClick={() => {
              onEdit({ addonProductId: Number(picked), groupId: null })
              setPicked('')
            }}
            className={cn('inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm', BUTTON_SECONDARY)}
            data-upsell-add-open
          >
            <Plus className="w-4 h-4" />
            <T>Pridať</T>
          </button>
        </div>

        {shown.length === 0 ? (
          <EmptyState
            icon={<Layers className="w-10 h-10" />}
            title={tt('Žiadne doplnky')}
            description={tt('Pridajte doplnok z katalógu a vyberte produkty, pri ktorých sa má ponúknuť.')}
          />
        ) : (
          <ReorderList
            entries={shown}
            keyOf={(item) => item.addonProductId}
            marker="upsell-item"
            disabled={order.isPending}
            onReorder={reorder}
          >
            {(item) => {
              const grouped = [...new Set(item.links.flatMap((link) => (link.groupId === null ? [] : [link.groupId])))]
              return (
                <button
                  type="button"
                  onClick={() => onEdit({ addonProductId: item.addonProductId, groupId: null })}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-1 py-0.5 text-left',
                    editing?.addonProductId === item.addonProductId && 'text-primary',
                  )}
                  data-upsell-item-open={item.addonProductId}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">
                      <T>{nameOf(item.addonProductId)}</T>
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      <T>pri produktoch</T>: {formatCount(item.links.length)}
                      {grouped.length > 0 && (
                        <>
                          {' · '}
                          {grouped.map(groupName).join(', ')}
                        </>
                      )}
                    </span>
                  </span>
                  <Edit2 aria-hidden className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                </button>
              )
            }}
          </ReorderList>
        )}
      </div>

      <div>
        {editing === null ? (
          <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            <T>Vyberte doplnok a zaškrtnite produkty, pri ktorých sa má ponúknuť.</T>
          </p>
        ) : (
          <ItemEditor
            key={`${String(editing.addonProductId)}:${String(editing.groupId)}`}
            addonProductId={editing.addonProductId}
            item={open}
            initialGroupId={editing.groupId}
            groups={groups}
            products={products}
            categories={categories}
            onClose={() => onEdit(null)}
          />
        )}
      </div>
    </div>
  )
}
